import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { websiteUrl } from 'src/constants/config';
import { showNotification } from 'src/redux/notification/notification.slice';
import { useShareSurveyGroupResultsMutation } from 'src/redux/survey/survey-groups.endpoints';
import { SurveyStatus } from 'src/redux/survey/survey.types';
import { useCopy } from 'src/hooks/useCopy';
import ErrorLogService from 'src/services/error-log/error-log.service';

const copySuccessMessage = 'Survey group link copied.';

interface UseShareSurveyGroupProps {
  surveyGroupId: string;
  surveyStatus: SurveyStatus | undefined;
}

interface UseShareSurveyGroupReturn {
  readonly canShare: boolean;
  readonly handleShare: () => void;
  readonly isLoading: boolean;
}

const useShareSurveyGroup = ({
  surveyGroupId,
  surveyStatus,
}: UseShareSurveyGroupProps): UseShareSurveyGroupReturn => {
  const shareUrl = useRef('');
  const hasGroup = Boolean(surveyGroupId);
  const canShare =
    hasGroup &&
    (surveyStatus === SurveyStatus.Live || surveyStatus === SurveyStatus.Complete);
  const [shareSurveyGroup, { data, error, isError, isLoading }] = useShareSurveyGroupResultsMutation();
  const { toClipboard } = useCopy();
  const dispatch = useDispatch();

  useEffect(() => {
    if (data && !shareUrl.current) {
      shareUrl.current = createUrl(data.share_id);
    }

    shareUrl.current && void toClipboard(shareUrl.current, copySuccessMessage);
  }, [data, toClipboard]);

  useEffect(() => {
    if (isError) {
      dispatch(showNotification({ theme: 'warning', summary: 'Something went wrong. Please try again.' }));
      ErrorLogService.logError(error ?? 'Share survey group error', 'useShareSurveyGroup', 'useEffect-isError');
    }
  }, [isError, error, dispatch]);

  function handleShare(): void {
    const url = window.location.href;

    if (url.indexOf('/survey/group-share/') >= 0) {
      shareUrl.current = url.split('?')[0] ?? url;
    }

    if (shareUrl.current) {
      void toClipboard(shareUrl.current, copySuccessMessage);
      return;
    }
    if (surveyGroupId) {
      void shareSurveyGroup(surveyGroupId);
    }
  }

  function createUrl(shareId: string): string {
    return `${websiteUrl}/survey/group-share/${shareId}`;
  }

  return {
    canShare,
    handleShare,
    isLoading,
  };
};

export default useShareSurveyGroup;
